import { Divider, Radio, Typography } from 'antd';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.css';
import { ReactComponent as RightArrow } from '../../../../Assets/sib/rightCircle-arrow.svg';
import { ReactComponent as Incoming } from '../../../../Assets/yesbank/incoming.svg';
import { ReactComponent as Investment } from '../../../../Assets/yesbank/investment.svg';
import { ReactComponent as OutGoing } from '../../../../Assets/yesbank/outgoing.svg';

import dayjs from 'dayjs';
import { AppContext } from '../../../../App';
import { EVENTCODES, pushEventToParent } from '../../../../Utils/eventUtils';
import {
  convertDateToMonthYearNumberFormat,
  formatToINR,
  getMonthsListFromAccountProfiles,
} from '../../../../Utils/utils';
import { getImagePath } from '../../../../Utils/assetUtils';
import CashFlowProjection from '../../CashFlowProjection';
import CategoryAnalysis from '../CategoryAnalysis';
import sibLogo from '../../../../Assets/sib/sib_logo.webp';

const CashFlow: React.FC<{ onOpenFAQ?: () => void }> = ({ onOpenFAQ }) => {
  const { isAccountDetailsFetched }: any = useContext(AppContext);
  const {
    data,
    accountsData,
    isCategoryAnalysisShown,
    setIsCategoryAnalysisShown,
    statusData,
    isAmountVisible,
  }: any = useContext(AppContext);

  const currentMonth = useMemo(
    () => dayjs(new Date()).format('MMM - YYYY'),
    [],
  );

  const [selectedAcconts, setSelectedAccounts] = useState<string[]>([]);

  const [open, setOpen] = useState(false);

  const [listOfAccounts, setListOfAccounts] = useState<any>({});

  const [selectedAccountsId, setSelectedAccountsId] = useState<any[]>([]);

  const [currentMonthYear, setCurrentMonthYear] = useState<any>(
    convertDateToMonthYearNumberFormat(),
  );
  const [totalValue, setTotalValue] = useState<any>({});

  const [type, setType] = useState('');
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);

  const [lastMonthsDetails, setLastMonthDetails] = useState<any>([]);


  const accountDropdownRef = useRef<HTMLDivElement>(null);
  const monthDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const totalValueLocal: any = {};
    lastMonthsDetails.forEach((res: any) => {
      totalValueLocal[res.month + '' + res.year] = {
        credit: 0,
        debit: 0,
        investment: 0,
      };
    });

    if (accountsData.current) {
      Object.keys(accountsData.current).forEach((acc: string) => {
        if (selectedAcconts.includes(acc)) {
          accountsData.current[acc].monthlyAnalysis.forEach((a: any) => {
            if (totalValueLocal[a.yearMonth]) {
              totalValueLocal[a.yearMonth] = {
                credit: a.creditAmount + totalValueLocal[a.yearMonth].credit,
                debit: a.debitAmount + totalValueLocal[a.yearMonth].debit,
                investment:
                  a.investment + totalValueLocal[a.yearMonth].investment,
              };
            }
          });
        }
      });

      Object.keys(totalValueLocal).forEach((value) => {
        totalValueLocal[value].debit =
          totalValueLocal[value].debit - totalValueLocal[value].investment;
      });
    }

    setTotalValue(totalValueLocal);
  }, [currentMonthYear, selectedAcconts, lastMonthsDetails]);

  // Dynamically derive available months from selected account(s) monthlyAnalytics in API response
  const monthsListFromApi = useMemo(
    () =>
      getMonthsListFromAccountProfiles(
        data?.current?.ACCOUNT_DETAILS?.accountProfileList || [],
        selectedAcconts,
      ),
    [data?.current?.ACCOUNT_DETAILS?.accountProfileList, selectedAcconts],
  );

  useEffect(() => {
    if (monthsListFromApi.length > 0) {
      setLastMonthDetails(monthsListFromApi);
      const exists = monthsListFromApi.some(
        (m: any) => `${m.month}${m.year}` === currentMonthYear,
      );
      if (!exists) {
        setCurrentMonthYear(
          `${monthsListFromApi[0].month}${monthsListFromApi[0].year}`,
        );
      }
    } else {
      setLastMonthDetails([]);
    }
  }, [monthsListFromApi]);

  const getIntialData = (accountData: any) => {
    let initListOfAccounts: any = {};
    let initSelectedAccounts: string[] = [];
    let initSelectedAccountsId: string[] = [];

    accountData.accountProfileList?.forEach((acc: any) => {
      const accounts = acc.account;

      if (
        statusData.current.accountDetails.filter((accountDetail: any) => {
          return (
            accountDetail.linkRefNumber === accounts.linkedAccRef &&
            accountDetail.status === 'ACTIVE'
          );
        }).length > 0
      ) {
        initListOfAccounts[accounts.linkedAccRef] = {
          number: accounts.number.slice(-6),
          id: accounts.id,
          bank: accounts.bank,
          fipId: accounts.fipId,
        };

        initSelectedAccountsId.push(accounts.id);
        initSelectedAccounts.push(accounts.linkedAccRef);
      }
    });

    setListOfAccounts(initListOfAccounts);
    setSelectedAccounts(initSelectedAccounts);
    setSelectedAccountsId(initSelectedAccountsId);
  };

  useEffect(() => {
    window.onpopstate = () => {
      if (isCategoryAnalysisShown) {
        setIsCategoryAnalysisShown(false);
      }
    };
    return () => {
      window.onpopstate = () => {};
    };
  }, [isCategoryAnalysisShown]);

  useEffect(() => {
    if (isAccountDetailsFetched && data.current.ACCOUNT_DETAILS) {
      getIntialData(data.current.ACCOUNT_DETAILS);
    }
  }, [isAccountDetailsFetched, data.current.ACCOUNT_DETAILS]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        accountDropdownRef.current &&
        !accountDropdownRef.current.contains(e.target as Node)
      ) {
        setAccountDropdownOpen(false);
      }
      if (
        monthDropdownRef.current &&
        !monthDropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentMonthLabel = useMemo(() => {
    const found = lastMonthsDetails.find(
      (el: any) => `${el.month}${el.year}` === currentMonthYear,
    );
    if (!found) return 'Month';
    return currentMonth === `${found.monthShortForm} - ${found.year}`
      ? 'This Month'
      : `${found.monthShortForm} - ${found.year}`;
  }, [currentMonthYear, lastMonthsDetails, currentMonth]);

  const handleImageError = (event: any) => {
    event.target.src = getImagePath('images/bank.svg');
  };

  return (
    <>
      {isCategoryAnalysisShown && (
        <div className={styles.horizontalSlidePanel}>
          <CategoryAnalysis defaultDataKey={type} onOpenFAQ={onOpenFAQ} />
        </div>
      )}

      <div className={`cashFlowSection ${styles.cashFlowWrapper}`}>
        <div className={`accountsSummary ${styles.accountsSummary}`}>
          <div className={styles.accountSummaryHeader}>
            <div>
              <Typography.Text className={styles.accountSummaryTitle}>
                My Accounts Summary
              </Typography.Text>
              <Typography.Text className={styles.accountSummarySubTitle}>
                A breakdown of your financials
              </Typography.Text>
            </div>
            <RightArrow
              className={styles.accountSummaryHeaderIcon}
              onClick={() => {
                pushEventToParent({ eventCode: EVENTCODES.SUMMARY_INCOMING });
                setType('CREDIT');
                setIsCategoryAnalysisShown(true);
              }}
            />
          </div>

          <div className={styles.selectsRow}>
            {/* Account custom dropdown */}
            <div
              className={styles.customSelectWrapper}
              ref={accountDropdownRef}
            >
              <div
                className={`${styles.customSelectTrigger} ${accountDropdownOpen ? styles.customSelectTriggerOpen : ''}`}
                onClick={() => setAccountDropdownOpen((v) => !v)}
              >
                <Typography.Text className={styles.accountLabelText}>
                  {selectedAcconts.length === Object.keys(listOfAccounts).length
                    ? 'All Accounts'
                    : `${selectedAcconts.length} ${selectedAcconts.length === 1 ? 'Account' : 'Accounts'}`}
                </Typography.Text>
                <svg
                  className={`${styles.chevron} ${accountDropdownOpen ? styles.chevronUp : ''}`}
                  width='12'
                  height='12'
                  viewBox='0 0 12 12'
                  fill='none'
                >
                  <path
                    d='M2 4L6 8L10 4'
                    stroke='#838383'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              {accountDropdownOpen && (
                <div
                  className={`${styles.customDropdownPanel} ${styles.dropdownContent}`}
                >
                  <div
                    className={styles.dropdownRow}
                    onClick={() => {
                      if (
                        selectedAcconts.length ===
                        Object.keys(listOfAccounts).length
                      ) {
                        setSelectedAccountsId([]);
                        setSelectedAccounts([]);
                      } else {
                        let accountLocal: any[] = [];
                        let accountIdLocal: any[] = [];
                        Object.keys(listOfAccounts).forEach((account) => {
                          accountIdLocal.push(listOfAccounts[account].id);
                          accountLocal.push(account);
                        });
                        setSelectedAccounts(accountLocal);
                        setSelectedAccountsId(accountIdLocal);
                      }
                    }}
                  >
                    <div className={styles.dropdownAccountRow}>
                      <Typography.Text
                        style={{
                          fontSize: 14,
                          color:
                            selectedAcconts.length ===
                            Object.keys(listOfAccounts).length
                              ? '#333333'
                              : '#838383',
                        }}
                      >
                        All Accounts
                      </Typography.Text>
                    </div>
                    <div
                      className={`${styles.customCheckbox} ${selectedAcconts.length === Object.keys(listOfAccounts).length ? styles.customCheckboxChecked : ''}`}
                    />
                  </div>

                  <Divider className={styles.filterDivider} />

                  {Object.keys(listOfAccounts).map((account, index) => (
                    <>
                      <div
                        key={account}
                        className={styles.dropdownRow}
                        onClick={() => {
                          const idx = selectedAcconts.indexOf(account);
                          const accountLocal = [...selectedAcconts];
                          const accountIdLocal = [...selectedAccountsId];
                          const listAccounts = listOfAccounts[account];
                          if (idx !== -1) {
                            if (selectedAcconts.length > 1) {
                              accountLocal.splice(idx, 1);
                              accountIdLocal.splice(listAccounts.id, 1);
                              setSelectedAccountsId(accountIdLocal);
                              setSelectedAccounts(accountLocal);
                            }
                          } else {
                            accountIdLocal.push(listAccounts.id);
                            accountLocal.push(account);
                            setSelectedAccountsId(accountIdLocal);
                            setSelectedAccounts(accountLocal);
                          }
                        }}
                      >
                        <div className={styles.dropdownAccountRow}>
                          <img
                            src={listOfAccounts[account].fipId === 'SIB' ? sibLogo : `https://logo.ignosis.ai/${listOfAccounts[account].fipId}.jpeg`}
                            className={styles.dropdownBankLogo}
                            onError={handleImageError}
                          />
                          <div className={styles.dropdownBankInfo}>
                            <Typography.Text
                              style={{
                                fontSize: 12,
                                color: selectedAcconts.includes(account)
                                  ? '#333333'
                                  : '#838383',
                                fontWeight: 600,
                              }}
                            >
                              {listOfAccounts[account].bank}
                            </Typography.Text>
                            <Typography.Text
                              className={styles.dropdownAccountNum}
                            >
                              XXXX {listOfAccounts[account].number.slice(-4)}
                            </Typography.Text>
                          </div>
                        </div>
                        <div
                          className={`${styles.customCheckbox} ${selectedAcconts.includes(account) ? styles.customCheckboxChecked : ''}`}
                        />
                      </div>
                      {index !== Object.keys(listOfAccounts).length - 1 && (
                        <Divider className={styles.filterDivider} />
                      )}
                    </>
                  ))}
                </div>
              )}
            </div>

            {/* Month custom dropdown */}
            <div className={styles.customSelectWrapper} ref={monthDropdownRef}>
              <div
                className={`${styles.customSelectTrigger} ${open ? styles.customSelectTriggerOpen : ''}`}
                onClick={() => setOpen((v) => !v)}
              >
                <Typography.Text className={styles.labelTypo}>
                  {currentMonthLabel}
                </Typography.Text>
                <svg
                  className={`${styles.chevron} ${open ? styles.chevronUp : ''}`}
                  width='12'
                  height='12'
                  viewBox='0 0 12 12'
                  fill='none'
                >
                  <path
                    d='M2 4L6 8L10 4'
                    stroke='#838383'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              {open && (
                <div
                  className={`${styles.customDropdownPanel} ${styles.dropdownContentMonth}`}
                >
                  {lastMonthsDetails.length === 0 && (
                    <div className={styles.dropdownRow}>
                      <Typography.Text style={{ fontSize: 14, color: '#838383' }}>
                        No months available
                      </Typography.Text>
                    </div>
                  )}
                  {lastMonthsDetails.map((element: any, index: number) => (
                    <>
                      <div
                        key={`${element.month}${element.year}`}
                        className={styles.dropdownRow}
                        onClick={() => {
                          setCurrentMonthYear(
                            `${element.month}${element.year}`,
                          );
                          setOpen(false);
                        }}
                      >
                        <div className={styles.dropdownAccountRow}>
                          <div className={styles.dropdownBankInfo}>
                            <Typography.Text
                              style={{
                                fontSize: 14,
                                color:
                                  currentMonthYear ===
                                  `${element.month}${element.year}`
                                    ? '#333333'
                                    : '#838383',
                              }}
                            >
                              {currentMonth ===
                              `${element.monthShortForm} - ${element.year}`
                                ? 'This Month'
                                : `${element.monthShortForm} - ${element.year}`}
                            </Typography.Text>
                          </div>
                        </div>
                        <Radio
                          className='radioMonthFilter'
                          checked={
                            currentMonthYear ===
                            `${element.month}${element.year}`
                          }
                          style={{ height: 16, width: 16 }}
                        />
                      </div>
                      {index !== lastMonthsDetails.length - 1 && (
                        <Divider className={styles.filterDivider} />
                      )}
                    </>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selectedAcconts.length > 0 && (
            <>
              <div
                className={`categories ${styles.categories}`}
                onClick={() => {

                  // pushEventToParent({ eventCode: EVENTCODES.SUMMARY_INCOMING });
                  // setType('CREDIT');
                  // setIsCategoryAnalysisShown(true);
                }}
              >
                <div className={styles.categoryIconRow}>
                  <Incoming />
                  <Typography.Text className={styles.categoryLabel}>
                    Incoming
                  </Typography.Text>
                </div>

                <div className={styles.categoryValueRow}>
                  <Typography.Text className={styles.categoryValue}>
                    {formatToINR(
                      totalValue &&
                        totalValue[currentMonthYear] &&
                        totalValue[currentMonthYear].credit
                        ? totalValue[currentMonthYear].credit.toString()
                        : '0',
                      isAmountVisible,
                    )}
                  </Typography.Text>

                  {/* <RightArrow /> */}
                </div>
              </div>

              <div
                className={`categories ${styles.categories}`}
                onClick={() => {
                  // pushEventToParent({ eventCode: EVENTCODES.SUMMARY_EXPENSES });

                  // setType('EXPENSE');
                  // setIsCategoryAnalysisShown(true);
                }}
              >
                <div className={styles.categoryIconRow}>
                  <OutGoing />
                  <Typography.Text className={styles.categoryLabel}>
                    Expenses
                  </Typography.Text>
                </div>

                <div className={styles.categoryValueRow}>
                  <Typography.Text className={styles.categoryValue}>
                    {formatToINR(
                      totalValue &&
                        totalValue[currentMonthYear] &&
                        totalValue[currentMonthYear].debit
                        ? totalValue[currentMonthYear].debit.toString()
                        : '0',
                      isAmountVisible,
                    )}
                  </Typography.Text>

                  {/* <RightArrow /> */}
                </div>
              </div>

              <div
                className={`categories ${styles.categories}`}
                onClick={() => {
                  // pushEventToParent({
                  //   eventCode: EVENTCODES.SUMMARY_INVESTMENT,
                  // });

                  // setType('INVESTMENT');
                  // setIsCategoryAnalysisShown(true);
                }}
              >
                <div className={styles.categoryIconRow}>
                  <Investment />
                  <Typography.Text className={styles.categoryLabel}>
                    Investment
                  </Typography.Text>
                </div>

                <div className={styles.categoryValueRow}>
                  <Typography.Text className={styles.categoryValue}>
                    {formatToINR(
                      totalValue &&
                        totalValue[currentMonthYear] &&
                        totalValue[currentMonthYear].investment
                        ? totalValue[currentMonthYear].investment.toString()
                        : '0',
                      isAmountVisible,
                    )}
                  </Typography.Text>
                  {/* <RightArrow /> */}
                </div>
              </div>
            </>
          )}
        </div>

        <CashFlowProjection
          value={totalValue && totalValue[currentMonthYear]}
          noAccountSelected={selectedAcconts.length === 0}
        />
      </div>
    </>
  );
};

export default CashFlow;
